/**
 * Lightweight first-party telemetry — captures click signals for the
 * upgrade-conversion funnel without a third-party SDK.
 *
 * Design contracts:
 *   • **Silent**     — telemetry failures NEVER surface as UX errors.
 *     Bad network, 5xx, parse error → swallow and move on.
 *   • **Batched**    — events accumulate and flush in groups of ≤10
 *     to avoid one POST per click. Idle flush after 5s keeps the
 *     latency low.
 *   • **Resilient**  — uses `navigator.sendBeacon` on tab-hide /
 *     before-unload so we don't lose the final click in a session.
 *   • **Capped**     — local buffer holds at most 50 events; older
 *     events are dropped if the client somehow goes offline forever.
 *
 * Public API:
 *   `trackEvent(name, props?)` — queue an event.
 *   `flushTelemetry()`         — fire any queued events now.
 *
 * Server contract (see `backend/routers/telemetry.py`):
 *   `POST /api/telemetry/events  {"events": [{event, props?, business_id?, client_ts?}]}`
 */
const FLUSH_INTERVAL_MS = 5000;
const BATCH_SIZE = 10;
const BUFFER_MAX = 50;
// Read straight from env to avoid importing axios-based apiClient,
// which uses ESM and breaks transitive Jest imports.
const BACKEND_URL =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
const ENDPOINT = `${BACKEND_URL}/api/telemetry/events`;

let buffer = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer || buffer.length === 0) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushTelemetry();
  }, FLUSH_INTERVAL_MS);
}

function takeBatch() {
  const batch = buffer.slice(0, BATCH_SIZE);
  buffer = buffer.slice(BATCH_SIZE);
  return batch;
}

async function postBatch(events, { useBeacon = false } = {}) {
  if (events.length === 0) return;
  const body = JSON.stringify({ events });
  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // Cookie / Authorization headers ride along automatically for
      // same-origin sendBeacon. Returns false if the browser refused
      // to queue (e.g. payload too large) — we just shrug.
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([body], { type: "application/json" }),
      );
      return;
    }
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      // `keepalive` so the request still finishes if the user navigates
      // away mid-fetch (modern browsers).
      keepalive: true,
      body,
    });
  } catch {
    // Network / CORS / abort — silently dropped. Telemetry never
    // surfaces as a UX failure.
  }
}

/**
 * Queue an event. Auto-flushes when the buffer hits BATCH_SIZE.
 * @param {string} name - snake_case event name (e.g. "upgrade_toast_shown")
 * @param {object} [props] - opaque props bag; primitives + short strings only
 * @param {object} [opts]
 * @param {string} [opts.businessId] - optional business context
 */
export function trackEvent(name, props = {}, opts = {}) {
  if (typeof name !== "string" || !name) return;
  const evt = {
    event: name.slice(0, 64),
    props: props || {},
    client_ts: Date.now(),
  };
  if (opts.businessId) evt.business_id = String(opts.businessId).slice(0, 80);
  buffer.push(evt);
  if (buffer.length > BUFFER_MAX) {
    buffer = buffer.slice(-BUFFER_MAX);
  }
  if (buffer.length >= BATCH_SIZE) {
    void flushTelemetry();
  } else {
    scheduleFlush();
  }
}

/** Flush any queued events immediately. Safe to call at any time. */
export async function flushTelemetry({ useBeacon = false } = {}) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (buffer.length > 0) {
    const batch = takeBatch();
    await postBatch(batch, { useBeacon });
    if (useBeacon) break; // Beacon = single shot during unload.
  }
}

// Auto-flush on tab hide / unload so we don't lose the final click of
// a session. Both events fire in modern browsers; we wire both for
// resilience across UAs.
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushTelemetry({ useBeacon: true });
    }
  });
  window.addEventListener("pagehide", () => {
    void flushTelemetry({ useBeacon: true });
  });
}

// Test-only — lets jest assertions inspect the queue.
export const __TEST_ONLY__ = {
  peekBuffer: () => [...buffer],
  resetBuffer: () => {
    buffer = [];
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  },
};
